import React, { FC, useEffect, useMemo, useState } from "react";
import { ReactComponent as PhoneIcon } from "@/assets/icons/phone_icon.svg";
import { Toast } from "@/components/Toast";
import { Box, Button, Flex, Input, InputGroup, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { loginStore } from "@/modules/login/store";
import { langStore, translate } from "@noli/business";
import Icon from "@/components/Icon";
import { themeStore } from "@/stores/theme";
import { isValidEmail, isValidPhone } from "@/utils/string";
import { Login_Words, Toast_Words } from "@/utils/lang";
import PasswordInput from "@/components/Input/password";
import uiConfig from "@/components/config";
import { useEventListener } from "ahooks";
import { useOwnToast } from "@/utils/misc";

const PasswordLogin = () => {
  const { loginBtnColor, loginBtnDisableColor, loginBtnTextColor } = themeStore.styles;
  const { visible } = loginStore!.loginState;
  const [loginAccount, setLoginAccount] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const lang = langStore.tgt;
  const toast = useOwnToast();
  useEffect(() => {
    if (visible) setPassword("");
  }, [visible]);

  useEventListener("keydown", (ev) => {
    if (loginStore.dialogType !== "account") return;
    if (ev.code === "Enter") {
      if (!loginEnable) {
        toast({
          description: translate(Toast_Words.loginInfoInvalid, lang),
          status: "warning",
          duration: 1500,
          position: "top",
        });
      } else {
        handlerLogin();
      }
    }
  });

  const accountVerification = useMemo(
    (): boolean => (lang === "zh" ? isValidPhone(loginAccount) : isValidEmail(loginAccount)),
    [loginAccount, lang],
  );

  const passwordVerification = useMemo((): boolean => password.length >= 6, [password]);

  const loginEnable = useMemo(
    () => accountVerification && passwordVerification,
    [accountVerification, passwordVerification],
  );

  const onAccountChange: React.ChangeEventHandler<{ value: string }> = (e) => {
    if (isValidEmail(e.target.value)) {
      loginStore?.setEmail(e.target.value);
    }
    if (isValidPhone(e.target.value)) {
      loginStore?.setPhone(e.target.value);
    }
    setLoginAccount(e.target.value);
  };

  const onPasswordChange: React.ChangeEventHandler<{ value: string }> = (e) => {
    setPassword(e.target.value);
  };

  const handleForgetPassword = () => {
    loginStore?.setDialogType("forgot");
  };

  const handleRegister = () => {
    loginStore.setDialogType("register");
  };

  const handlerLogin = () => {
    setLoading(true);
    const isPhone = isValidPhone(loginAccount);
    loginStore
      ?.loginByPassword({
        username: isPhone ? loginAccount : "",
        password: password,
        email: isPhone ? "" : loginAccount,
      })
      .then(async (res: any) => {
        const { is_new } = res;
        if (res.errCode == 0) {
          Toast({
            title: translate(Toast_Words.loginSuccess, lang),
            status: "success",
          });
          loginStore?.closeLoginDialog();
        } else {
          Toast({
            title: translate(Toast_Words.loginFailed, lang),
            status: "error",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box w="100%">
      <Text mt="16px" mb="8px" fontSize="14px" color={"#4E5969"}>
        {lang === "zh"
          ? translate(Login_Words.phoneNum, lang)
          : translate(Login_Words.emailAddr, lang)}
      </Text>
      <Input
        borderColor="fill.emphasis"
        _focusVisible={{ borderColor: loginBtnColor }}
        _placeholder={{ color: "#C9CDD4" }}
        color="inherit"
        fontSize={uiConfig.inputFontSize}
        value={loginAccount}
        onChange={onAccountChange}
        placeholder={
          lang === "zh"
            ? translate(Login_Words.phonePhd, lang)
            : translate(Login_Words.emailPhd, lang)
        }
      />
      <Text mt="16px" mb="8px" fontSize="14px" color={"#4E5969"}>
        {translate(Login_Words.password, lang)}
      </Text>
      <PasswordInput
        _focusVisible={{ borderColor: loginBtnColor }}
        value={password}
        placeholder={translate(Login_Words.passwordPhd, lang)}
        onChange={onPasswordChange}
      />
      <Flex justifyContent="end" color={loginBtnColor} mt="1em" fontSize={"12px"}>
        <Text cursor="pointer" mr="1em" onClick={handleRegister}>
          {translate(Login_Words.registerNewAccount, lang)}
        </Text>
        <Text cursor="pointer" onClick={handleForgetPassword}>
          {translate(Login_Words.forgotPassword, lang)}
        </Text>
      </Flex>
      <Button
        type="button"
        size="md"
        w="100%"
        rounded="full"
        mt="40px"
        bgColor={loginBtnColor}
        color={loginBtnTextColor}
        _disabled={{
          bgColor: loginBtnDisableColor,
        }}
        _hover={{}}
        _focus={{}}
        _active={{}}
        style={{ fontWeight: 600, marginBottom: "21px", height: "40px" }}
        disabled={!loginEnable}
        loadingText={translate(Login_Words.login, lang) + "..."}
        onClick={handlerLogin}
        isLoading={loading}
      >
        {translate(Login_Words.login, lang)}
      </Button>
    </Box>
  );
};

export default observer(PasswordLogin);
